import type { FileData, StandaloneVseMetadataArtifact } from "../../types";
import type { VSEFileMetaInfo } from "../../types/unity";
import { ApiError } from "../api/base";
import { apiService } from "../apiService";
import { resolveSseToken } from "../sseToken";

const INVALID_METADATA_ERROR = "Import failed.";

export async function loadStandaloneVseMetadata(
  artifact: StandaloneVseMetadataArtifact | null | undefined,
): Promise<VSEFileMetaInfo | undefined> {
  if (!artifact) return undefined;
  if (!artifact.download_url) throw new Error(INVALID_METADATA_ERROR);

  try {
    const response = await fetch(artifact.download_url, { cache: "no-store" });
    if (!response.ok) throw new Error();
    const metadata = (await response.json()) as Record<string, unknown>;
    const materials = metadata.materials;
    const materialCount = metadata.materialCount;
    const contentHash = metadata.contentHash;
    if (
      metadata.vseMetaVersion !== artifact.schema_version ||
      typeof contentHash !== "string" ||
      contentHash.toLowerCase() !== artifact.content_hash.toLowerCase() ||
      !Array.isArray(metadata.fileTree) ||
      !Array.isArray(materials) ||
      typeof materialCount !== "number" ||
      !Number.isInteger(materialCount) ||
      materialCount !== artifact.material_count ||
      materialCount !== materials.length
    ) {
      throw new Error();
    }
    return metadata as VSEFileMetaInfo;
  } catch {
    throw new Error(INVALID_METADATA_ERROR);
  }
}

interface UploadItemMetadataSource {
  jobId?: string | null;
  fileData?: FileData | Record<string, unknown> | null;
}

/**
 * Unity 로드에 쓰는 vseFileMetaInfo를 돌려준다.
 *
 * 변환 직후에는 메모리의 fileData에 있다. 새로고침 뒤에는 upload-store가 이 필드를
 * 영속하지 않으므로 fileData.metadata_artifact로 다시 받고, 서명 URL이 만료됐으면
 * Job 상태에서 artifact를 새로 받아 재시도한다. 원본 Job에 artifact가 없거나 Job이
 * 없는 Library Import는 Import 시작 때와 같이 Main API sidecar 확보로 다시 받는다
 * (STUDIO-ASSET-003 Library attach completion contract). artifact 자체가 없는 legacy
 * 항목은 기존처럼 metadata 없이 로드한다.
 */
export async function resolveUploadItemVseFileMetaInfo(
  item: UploadItemMetadataSource,
): Promise<VSEFileMetaInfo | undefined> {
  const fileData = (item.fileData ?? null) as Record<string, unknown> | null;
  const inMemory = fileData?.vseFileMetaInfo as VSEFileMetaInfo | null | undefined;
  if (inMemory) return inMemory;
  const artifact = (fileData?.metadata_artifact ?? null) as StandaloneVseMetadataArtifact | null;
  if (!artifact) return undefined;
  const libraryObjectId =
    typeof fileData?.library_object_id === "string" ? fileData.library_object_id : null;
  try {
    return await loadStandaloneVseMetadata(artifact);
  } catch (error) {
    if (!item.jobId && !libraryObjectId) throw error;
  }
  const token = await resolveSseToken();
  if (item.jobId) {
    let job: { metadata_artifact?: StandaloneVseMetadataArtifact | null } | null = null;
    try {
      job = (await apiService.getConversionJobStatus(item.jobId, token)) as typeof job;
    } catch (error) {
      // Legacy Library rows may outlive their source conversion Job.
      if (!libraryObjectId || !(error instanceof ApiError) || error.status !== 404) {
        throw new Error(INVALID_METADATA_ERROR);
      }
    }
    if (job?.metadata_artifact) return loadStandaloneVseMetadata(job.metadata_artifact);
  }
  if (libraryObjectId) {
    return loadStandaloneVseMetadata(await ensureLibraryVseMetadataArtifact(libraryObjectId, token));
  }
  return loadStandaloneVseMetadata(artifact);
}

const ENSURE_POLL_INTERVAL_MS = 2_000;
// ponytail: 고정 3분 상한. 더 오래 걸리는 VSE가 나오면 polling 대신 SSE 구독으로 바꾼다.
const ENSURE_TIMEOUT_MS = 180_000;

/**
 * sidecar가 없는 기존 Library VSE의 metadata identity를 Main API로 확보한다.
 *
 * sidecar가 이미 있으면 Converter가 접수 응답 전에 결과를 돌려주므로 대개 왕복
 * 한 번으로 끝난다. 새로 만들어야 하면 Job 완료까지 기다린다. 확보하지 못하면
 * 빈 metadata로 로드하지 않고 `Import failed.`로 끝낸다.
 */
export async function ensureLibraryVseMetadataArtifact(
  libraryObjectId: string,
  userToken: string | null,
): Promise<StandaloneVseMetadataArtifact> {
  let submitted;
  try {
    submitted = await apiService.ensureLibraryVseMetadata(libraryObjectId, userToken);
  } catch {
    throw new Error(INVALID_METADATA_ERROR);
  }
  if (submitted?.metadata_artifact) return submitted.metadata_artifact;

  const jobId = submitted?.job_id;
  if (!jobId) throw new Error(INVALID_METADATA_ERROR);

  const deadline = Date.now() + ENSURE_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, ENSURE_POLL_INTERVAL_MS));
    let job;
    try {
      job = (await apiService.getConversionJobStatus(jobId, userToken)) as {
        status?: string;
        metadata_artifact?: StandaloneVseMetadataArtifact | null;
      } | null;
    } catch {
      throw new Error(INVALID_METADATA_ERROR);
    }
    if (job?.metadata_artifact) return job.metadata_artifact;
    if (job?.status === "vse_metadata_failed") break;
  }
  throw new Error(INVALID_METADATA_ERROR);
}
